<template>
    <page :headline="headline">

        <loading v-show="loading"></loading>

        <div class="mb-8">
            <h2>Anfragen</h2>

            <p v-show="pendingBookings.length === 0">Keine aktuellen Anfragen.</p>

            <tailwind-table :header="['User', 'Zeitraum', 'Status','']" v-show="pendingBookings.length > 0">
                <tr v-for="(booking, i) in pendingBookings" :class="{'bg-gray-50': i % 2}">
                    <td class="px-6 py-3">
                        <span v-if="booking.user">{{ booking.user.first_name }} {{ booking.user.name }}</span>
                    </td>
                    <td class="px-6 py-3">
                        {{ booking.start | dayOfWeek }} - {{ booking.start | date }} {{ booking.start | time }} - {{ booking.end | time }} Uhr
                    </td>
                    <td class="px-6 py-3"
                        :class="{
                            'text-green-500': booking.status === 'confirmed',
                            'text-red-500': booking.status === 'denied',
                            'text-yellow-500': booking.status === 'pending'
                        }">
                        {{ booking.status }}
                    </td>
                    <td class="px-6 py-3 text-right">
                        <button-secondary @click="confirm(booking.id)">
                            <i class="fa fa-check text-green-500 mr-2"></i> Bestätigen
                        </button-secondary>
                        <button-secondary @click="decline(booking.id)">
                            <i class="fa fa-times text-red-500 mr-2"></i> Ablehnen
                        </button-secondary>
                    </td>
                </tr>
            </tailwind-table>
        </div>

        <div class="mb-8" >
            <h2>Bestätigte Buchungen</h2>

            <p v-show="confirmedBookings.length === 0">Keine Buchungen gefunden.</p>

            <tailwind-table :header="['User', 'Zeitraum', 'Kommentar', '']" v-show="confirmedBookings.length > 0">
                <tr v-for="(booking, i) in confirmedBookings" :class="{'bg-gray-50': i % 2}" :key="booking.id">
                    <td class="px-6 py-3">
                        <span v-if="booking.user">{{ booking.user.first_name }} {{ booking.user.name }}</span>
                    </td>
                    <td class="px-6 py-3">
                        {{ date(booking.start) }} {{ time(booking.start) }} -
                        <span v-show="date(booking.start) !== date(booking.end)">
                            {{ date(booking.end)}}
                        </span>
                        {{ time(booking.end) }} Uhr
                    </td>
                    <td class="px-6 py-3">
                        {{ booking.comment ?? '-' }}
                    </td>
                    <td class="px-6 py-3 flex justify-end">
                        <button-secondary @click="decline(booking.id)" class="mr-2">
                            <i class="fa fa-times text-red-500 mr-2"></i> Absagen
                        </button-secondary>
                        <bookable-booking-edit :booking="booking"></bookable-booking-edit>
                    </td>
                </tr>
            </tailwind-table>
        </div>


        <div v-show="deniedBookings.length > 0">
            <h2>Abgelehnte Buchungen</h2>

            <tailwind-table :header="['User', 'Zeitraum','']">
                <tr v-for="(booking, i) in deniedBookings" :class="{'bg-gray-50': i % 2}">
                    <td class="px-6 py-3">
                        <span v-if="booking.user">{{ booking.user.first_name }} {{ booking.user.name }}</span>
                    </td>
                    <td class="px-6 py-3">
                        {{ date(booking.start) }} {{ time(booking.start) }} -
                        <span v-show="date(booking.start) !== date(booking.end)">
                            {{ date(booking.end)}}
                        </span>
                        {{ time(booking.end) }} Uhr
                    </td>
                    <td class="px-6 py-3 text-right">
                        <button-secondary @click="confirm(booking.id)">
                            <i class="fa fa-check text-green-500 mr-2"></i> Buchung wiederherstellen
                        </button-secondary>
                    </td>
                </tr>
            </tailwind-table>
        </div>

    </page>
</template>

<script>
import Page from "../components/UI/Page.vue";
import Loading from "../components/UI/Loading.vue";
import TailwindTable from "../components/UI/TailwindTable.vue";
import ButtonSecondary from "../components/UI/ButtonSecondary.vue";
import BookableBookingEdit from "../components/Bookable/BookableBookingEdit.vue";

export default {
    name: "Bookable",

    components: {BookableBookingEdit, ButtonSecondary, TailwindTable, Loading, Page},

    props: ['id'],

    data: () => ({
        loading: true,
        bookable: {},
        bookings: [],
    }),

    mounted() {
        this.load();
    },

    computed: {
        headline() {
            return this.bookable?.name;
        },
        pendingBookings() {
            return this.bookings.filter(b => b.status === 'pending');
        },
        deniedBookings() {
            return this.bookings.filter(b => b.status === 'denied');
        },
        confirmedBookings() {
            return this.bookings.filter(b => b.status === 'confirmed');
        },
    },

    filters: {
        time(val) {
            return val.substr(11, 5);
        },
        date(val) {
            let [y, m, d] = val.substr(0, 10).split('-');
            return `${d}.${m}.${y}`;
        },
        dayOfWeek(val) {
            let [y, m, d] = val.substring(0, 10).split('-');
            return new Date(`${y}-${m}-${d}`).toLocaleString('de-de', {  weekday: 'short' });
        }
    },

    methods: {
        load() {
            this.loading = true;
            this.$http
                .get(`/admin/bookables/${this.id}`)
                .then(result => {
                    this.bookable = result.data.data;
                    this.bookings = result.data.bookings;
                    this.loading = false;
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.loading = false;
                });
        },
        confirm(id) {
            this.updateStatus(id, 'confirmed');
        },
        decline(id) {
            this.updateStatus(id, 'denied');
        },
        updateStatus(id, status) {
            this.loading = true;
            this.$http
                .put(`/admin/bookable_bookings/${id}`, {
                    status: status,
                })
                .then(() => {
                    if (status === 'confirmed') {
                        this.$root.$emit('alert', 'Buchung wurde bestätigt.');
                    }
                    if (status === 'denied') {
                        this.$root.$emit('alert', 'Buchung wurde abgelehnt.');
                    }

                    this.load();
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.loading = false;
                });
        },
        time(val) {
            return val.substr(11, 5);
        },
        date(val) {
            let [y, m, d] = val.substr(0, 10).split('-');
            return `${d}.${m}.${y}`;
        },
    },
}
</script>
